import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Headphones, Search, Filter, Clock, CheckCircle2, AlertCircle,
  MessageSquare, Paperclip, Send, X, ChevronRight, ArrowLeft,
  Loader2, Trash2, User, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const STATUS_CONFIG = {
  open: { label: 'Abierto', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  in_progress: { label: 'En proceso', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  waiting: { label: 'Esperando', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  resolved: { label: 'Resuelto', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  closed: { label: 'Cerrado', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
};

const CATEGORY_CONFIG = {
  general: { label: 'General', emoji: '💬' },
  technical: { label: 'Técnico', emoji: '🔧' },
  billing: { label: 'Facturación', emoji: '💳' },
  feature: { label: 'Sugerencia', emoji: '💡' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Baja', color: 'text-gray-400' },
  normal: { label: 'Normal', color: 'text-blue-400' },
  high: { label: 'Alta', color: 'text-amber-400' },
  urgent: { label: 'Urgente', color: 'text-red-400' }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  });
};

const AdminSoporte = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Reply
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await api.get(`/support/admin/tickets?${params}`);
      setTickets(res.data.tickets || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error('Error cargando tickets');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, categoryFilter, searchQuery]);

  const fetchTicketDetail = async (ticketId) => {
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setSelectedTicket(res.data);
    } catch (err) {
      toast.error('Error cargando ticket');
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleUpdateStatus = async (status) => {
    try {
      await api.put(`/support/admin/tickets/${selectedTicket.id}`, { status });
      toast.success('Estado actualizado');
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
    } catch (err) {
      toast.error('Error actualizando');
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    setSendingReply(true);
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/reply`, { message: replyMessage });
      toast.success('Respuesta enviada');
      setReplyMessage('');
      fetchTicketDetail(selectedTicket.id);
    } catch (err) {
      toast.error('Error enviando respuesta');
    } finally {
      setSendingReply(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!confirm('¿Eliminar este ticket?')) return;
    try {
      await api.delete(`/support/admin/tickets/${selectedTicket.id}`);
      toast.success('Ticket eliminado');
      setSelectedTicket(null);
      fetchTickets();
    } catch (err) {
      toast.error('Error eliminando');
    }
  };

  // Ticket Detail View
  if (selectedTicket) {
    return (
      <div className="min-h-screen bg-app-bg p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => setSelectedTicket(null)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-text-muted">#{selectedTicket.id}</span>
                  <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_CONFIG[selectedTicket.status]?.color}`}>
                    {STATUS_CONFIG[selectedTicket.status]?.label}
                  </span>
                  <span className={`text-sm ${PRIORITY_CONFIG[selectedTicket.priority]?.color}`}>
                    ● {PRIORITY_CONFIG[selectedTicket.priority]?.label}
                  </span>
                </div>
                <h1 className="text-xl font-bold text-text-main">{selectedTicket.subject}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selectedTicket.status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
                className="h-9 px-3 rounded-lg bg-surface border border-border-subtle text-text-main text-sm"
              >
                {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
              <Button variant="outline" size="sm" onClick={handleDeleteTicket} className="text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-surface border border-border-subtle rounded-xl p-4 mb-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-brand/20 flex items-center justify-center text-brand font-bold">
              {selectedTicket.user_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-medium text-text-main">{selectedTicket.user_name}</p>
              <p className="text-sm text-text-muted">{selectedTicket.user_email}</p>
            </div>
            <div className="ml-auto text-right text-sm text-text-muted">
              <p>Creado: {formatDate(selectedTicket.created_at)}</p>
              <p>{CATEGORY_CONFIG[selectedTicket.category]?.emoji} {CATEGORY_CONFIG[selectedTicket.category]?.label}</p>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden mb-4">
            <div className="divide-y divide-border-subtle">
              {selectedTicket.messages?.map((msg) => (
                <div key={msg.id} className={`p-4 ${msg.sender === 'admin' ? 'bg-brand/5' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      msg.sender === 'admin' ? 'bg-brand text-white' : 'bg-surface-hover text-text-main'
                    }`}>
                      {msg.sender === 'admin' ? '👨‍💼' : msg.sender_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-text-main">
                          {msg.sender === 'admin' ? 'Soporte FASCINAI' : msg.sender_name}
                        </span>
                        <span className="text-xs text-text-muted">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-text-main whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {msg.attachments.map(att => (
                            <span key={att.id} className="flex items-center gap-1 px-2 py-1 bg-app-bg rounded text-xs text-text-muted">
                              <Paperclip className="w-3 h-3" /> {att.filename}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reply */}
          {selectedTicket.status !== 'closed' && (
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <Textarea
                placeholder="Escribe tu respuesta..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                className="min-h-[100px] mb-3 bg-app-bg border-border-subtle"
              />
              <div className="flex justify-end">
                <Button onClick={handleSendReply} disabled={sendingReply || !replyMessage.trim()} className="bg-brand">
                  {sendingReply ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Enviar respuesta
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tickets List
  return (
    <div className="min-h-screen bg-app-bg p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/master')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-main flex items-center gap-2">
                <Headphones className="w-7 h-7 text-brand" />
                Gestión de Soporte
              </h1>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
            <button
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              className={`p-3 rounded-xl border transition-all ${
                statusFilter === key ? val.color + ' border-current' : 'bg-surface border-border-subtle hover:border-brand/30'
              }`}
            >
              <p className="text-2xl font-bold text-text-main">{stats[key] || 0}</p>
              <p className="text-xs text-text-muted">{val.label}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              placeholder="Buscar por ID, asunto o email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-surface border-border-subtle"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="h-10 px-3 rounded-lg bg-surface border border-border-subtle text-text-main"
          >
            <option value="">Todas las categorías</option>
            {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
              <option key={key} value={key}>{val.emoji} {val.label}</option>
            ))}
          </select>
          <Button variant="outline" onClick={fetchTickets}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>

        {/* Tickets Table */}
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-app-bg/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted">TICKET</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted">USUARIO</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted">CATEGORÍA</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted">ESTADO</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted">PRIORIDAD</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted">ACTUALIZADO</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-text-muted">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </td></tr>
              ) : tickets.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-text-muted">No hay tickets</td></tr>
              ) : (
                tickets.map(ticket => (
                  <tr 
                    key={ticket.id} 
                    onClick={() => fetchTicketDetail(ticket.id)}
                    className="hover:bg-app-bg/50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <p className="text-xs text-text-muted">#{ticket.id}</p>
                      <p className="font-medium text-text-main truncate max-w-[200px]">{ticket.subject}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-text-main">{ticket.user_name}</p>
                      <p className="text-xs text-text-muted">{ticket.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {CATEGORY_CONFIG[ticket.category]?.emoji} {CATEGORY_CONFIG[ticket.category]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs border ${STATUS_CONFIG[ticket.status]?.color}`}>
                        {STATUS_CONFIG[ticket.status]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${PRIORITY_CONFIG[ticket.priority]?.color}`}>
                        ● {PRIORITY_CONFIG[ticket.priority]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-muted">{formatDate(ticket.updated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSoporte;
